<template>
  <div class="xrow">
    <h2>{{ titleAdd }}</h2>

    <div class="search-select">
      <input
        class="form-control"
        :placeholder="placeholder"
        type="text"
        v-model="searchTerm"
        @input="debouncedSearch"
        :disabled="isDisabledSearch"
      />

      <ul class="list">
        <li
          class="list-item"
          v-for="item in formattedItems"
          :key="item[itemKey]"
          :class="{ 'item-disabled': item.disabled }"
          @click="() => handleSelect(item)"
        >
          <div class="item-content">
            <span>
              {{ item.idExibicao || item.id }} -
              {{ truncateText(item.descComp || item.descNF || item.desc, 80) }}
            </span>
          </div>

          <button
            class="button button-primary add-item"
            title="Adicionar"
            v-if="!item.disabled"
            :disabled="isDisabledAdd(item)"
          >
            &nbsp;+&nbsp;
          </button>
        </li>

        <li v-if="isLoading">Carregando...</li>
        <li v-if="!isLoading && !items.length && searchTerm">
          Nenhum item encontrado.
        </li>
      </ul>
    </div>

    <h2>{{ titleSelected }}</h2>
    <div class="table-container">
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!itemsSelected.length">
              <td :colspan="columns.length + 1" style="text-align: center;">
                {{ emptyMessage }}
              </td>
            </tr>

            <tr v-for="(item, index) in itemsSelected" :key="item[itemKey] || index">
              <td v-for="col in columns" :key="col.key">
                <template v-if="col.type === 'number'">
                  <input
                    class="form-control"
                    type="number"
                    min="1"
                    style="width: 150px;"
                    :value="item[col.key]"
                    @input="updateField(index, col.key, $event.target.value)"
                    :disabled="isDisabledTable"
                  />
                </template>

                <template v-else-if="col.type === 'percent'">
                  <input-percent
                    v-model="item[col.key]"
                    style="width: 150px; text-align: right;"
                    :disabled="isDisabledTable"
                  ></input-percent>
                </template>

                <template v-else>
                  {{ item[col.key] }}
                </template>
              </td>

              <td>
                <button
                  class="button button-error"
                  title="Remover"
                  @click="() => handleDeselect(item, index)"
                  :disabled="isDisabledTable"
                >
                  <strong>&times;</strong>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import _debounce from "lodash/debounce";

export default {
  name: "SelectableListBase",
  props: {
    titleAdd: { type: String, required: true },
    titleSelected: { type: String, required: true },
    placeholder: { type: String, default: "Pesquisar por ID, descrição ou referência" },
    items: { type: Array, required: true },
    itemsSelected: { type: Array, required: true },
    itemKey: { type: String, default: "id" },
    columns: { type: Array, required: true },
    loadItems: { type: Function, required: true },
    selectCallback: { type: Function, required: true },
    deselectCallback: { type: Function, required: true },
    itemTransform: { type: Function, required: true },
    updateQuantityCallback: { type: Function, default: null },
    disableCondition: { type: Function, default: null }, // define item.disabled
    disableAddCondition: { type: Function, default: null },
    emptyMessage: { type: String, default: "Nenhum item selecionado." },
    isDisabledSearch: { type: Boolean, default: false },
    isDisabledTable: { type: Boolean, default: false },
  },
  data() {
    return {
      searchTerm: "",
      isLoading: false,
      debouncedSearch: null,
    };
  },
  computed: {
    formattedItems() {
      return this.items.map((item) => ({
        ...item,
        disabled: this.disableCondition ? this.disableCondition(item, this.itemsSelected) : false,
      }));
    },
  },
  created() {
    this.debouncedSearch = _debounce(() => {
      this.isLoading = true;
      Promise.resolve(this.loadItems(this.searchTerm))
        .finally(() => (this.isLoading = false));
    }, 500);
    this.loadItems("");
  },
  methods: {
    truncateText(text = "", length = 80) {
      const str = String(text);
      return str.length <= length ? str : str.substring(0, length) + "...";
    },
    handleSelect(item) {
      if (item.disabled) return;
      const newItem = this.itemTransform(item);
      this.selectCallback(newItem);
    },
    handleDeselect(item, index) {
      this.deselectCallback(item[this.itemKey], index);
    },
    updateField(index, key, value) {
      const parsedValue = key === "qtde" ? parseInt(value, 10) || 1 : value;
      if (this.updateQuantityCallback) {
        this.updateQuantityCallback(index, parsedValue);
      } else {
        this.$set(this.itemsSelected[index], key, parsedValue);
      }
    },
    isDisabledAdd(item) {
      if (this.disableAddCondition) return this.disableAddCondition(item);
      return this.isDisabledTable;
    },
  },
};
</script>

<style scoped>
.table-container {
  max-height: 230px;
  overflow-y: auto;
}
.table tbody > tr:nth-child(even) td {
  background-color: #fff;
}
.table tbody > tr:nth-child(odd) td {
  background-color: #f1f1f1;
}
.table tbody > tr > td {
  padding: 6px;
}
.list {
  padding: 10px 0;
  height: 200px;
  overflow-y: auto;
  border: 1px solid #ccc;
  background-color: #fff;
}
.list-item {
  font-size: 14px;
  padding: 8px;
  cursor: pointer;
  position: relative;
  border-bottom: 1px solid #eee;
}
.list-item:hover {
  background-color: #ddd !important;
}
.list-item.item-disabled {
  opacity: 0.6;
  background-color: #f0f0f0;
  cursor: not-allowed;
}
.add-item {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 1px 5px;
}
.item-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 50px;
}
.search-select {
  margin-bottom: 20px;
}
</style>
